'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from 'next-intl';
import { FriendlyCaptchaProps, useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import { requestWithdrawalLink } from '@/server-functions/withdrawal/withdrawal-actions';
import { loadWithdrawalCountryCodes } from '@/server-functions/withdrawal/withdrawal-countries';
import { WithdrawalRequestOutcome } from '@/server-functions/withdrawal/withdrawal-types';
import Button from '@/ui/components/shared/Button';
import Container from '@/ui/components/shared/Container';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import { ListSelect } from '@/ui/components/pages/checkout/address-form/list-select';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useLocalization } from '@/ui/providers/localizationProvider';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { useWithdrawalStage } from '../withdrawal-stage';
import {
  WithdrawalRequestFormData,
  WithdrawalRequestFormTileType,
} from './types/withdrawal-request-form-tile-type';
import { buildWithdrawalCountryOptions } from './withdrawal-country-options';

type TileProps = Readonly<{ tile: WithdrawalRequestFormTileType; trackingPrefix?: string }>;

type ErrorOutcome = Exclude<WithdrawalRequestOutcome, 'sent'>;

type ErrorTextKey =
  | 'error-not-found'
  | 'error-window-exceeded'
  | 'error-already-withdrawn'
  | 'error-not-eligible'
  | 'error-country-not-supported'
  | 'error-generic';

const ERROR_TEXT_KEY: Record<ErrorOutcome, ErrorTextKey> = {
  'not-found': 'error-not-found',
  'window-exceeded': 'error-window-exceeded',
  'already-withdrawn': 'error-already-withdrawn',
  'not-eligible': 'error-not-eligible',
  'country-not-supported': 'error-country-not-supported',
  'captcha-failed': 'error-generic',
  error: 'error-generic',
};

type Translator = (key: string) => string;

// Limits mirror the service-api request DTO.
const WITHDRAWAL_FIELD_LIMITS = {
  orderReference: 64,
  lastName: 100,
  postalCode: 20,
} as const;

function buildSchema(tForm: Translator, tCheckout: Translator) {
  return yup.object({
    lastName: yup
      .string()
      .trim()
      .required(tCheckout('Last name is required'))
      .max(WITHDRAWAL_FIELD_LIMITS.lastName, tCheckout('Last name is too long')),
    orderReference: yup
      .string()
      .trim()
      .required(tForm('This field is required'))
      .max(WITHDRAWAL_FIELD_LIMITS.orderReference, tCheckout('Order reference is too long')),
    postalCode: yup
      .string()
      .trim()
      .required(tCheckout('Postal code is required'))
      .max(WITHDRAWAL_FIELD_LIMITS.postalCode, tCheckout('Postal code is too long')),
    country: yup
      .string()
      .required(tCheckout('Country is required'))
      .matches(/^[A-Z]{2}$/, tCheckout('Country is required')),
    consent: yup
      .boolean()
      .oneOf([true], tForm('This field is required'))
      .required(tForm('This field is required')),
  });
}

function WithdrawalRequestForm({ tile, trackingPrefix }: TileProps) {
  const tForm = useTranslations('DoubleOptIn.Form');
  const tCheckout = useTranslations('CheckoutForm');
  const { language } = useLocalization();
  const [isPending, setIsPending] = useState(false);
  const [outcome, setOutcome] = useState<WithdrawalRequestOutcome | null>(null);
  const [countryCodes, setCountryCodes] = useState<string[] | null>(null);
  const countries = useMemo(
    () => (countryCodes ? buildWithdrawalCountryOptions(countryCodes, language) : null),
    [countryCodes, language],
  );

  useEffect(() => {
    let ignore = false;
    loadWithdrawalCountryCodes()
      .catch((): string[] => [])
      .then((codes) => {
        if (!ignore) {
          setCountryCodes(codes);
        }
      });
    return () => {
      ignore = true;
    };
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<WithdrawalRequestFormData>({
    resolver: yupResolver(buildSchema(tForm, tCheckout)),
    defaultValues: {
      lastName: '',
      orderReference: '',
      postalCode: '',
      country: '',
      consent: false,
    },
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: language as FriendlyCaptchaProps['language'],
    showAttribution: true,
  });
  const captchaSolution = captchaHook.captchaStatus.solution;

  const onSubmit = async (values: WithdrawalRequestFormData) => {
    if (!captchaSolution || isPending) {
      return;
    }
    DataLayerHelper.pushWithdrawalStep(2, 'public');
    setIsPending(true);
    setOutcome(null);
    let result: WithdrawalRequestOutcome;
    try {
      result = await requestWithdrawalLink({
        orderReference: values.orderReference,
        lastName: values.lastName,
        postalCode: values.postalCode,
        country: values.country,
        captchaSolution,
      });
    } catch {
      result = 'error';
    }
    setIsPending(false);
    setOutcome(result);
    if (result !== 'sent') {
      // A Friendly Captcha solution is single-use; every rejected attempt needs a fresh one.
      captchaHook.resetWidget();
    }
  };

  if (outcome === 'sent') {
    return <RichTextRenderer blocks={tile['success-text']} className="text-md sm:text-lg" />;
  }

  const errorText = outcome ? tile[ERROR_TEXT_KEY[outcome]] : undefined;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div
        data-testid="withdrawal-fields-grid"
        className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-12"
      >
        <TextInput
          id="lastName"
          register={register}
          errors={errors.lastName}
          label={tile['last-name-label']?.text ?? ''}
          placeholder={tile['last-name-label']?.text ?? ''}
          maxLength={WITHDRAWAL_FIELD_LIMITS.lastName}
          required
        />
        <TextInput
          id="orderReference"
          register={register}
          errors={errors.orderReference}
          label={tile['order-reference-label']?.text ?? ''}
          placeholder={tile['order-reference-label']?.text ?? ''}
          maxLength={WITHDRAWAL_FIELD_LIMITS.orderReference}
          required
        />
        <TextInput
          id="postalCode"
          register={register}
          errors={errors.postalCode}
          label={tile['postal-code-label']?.text ?? ''}
          placeholder={tile['postal-code-label']?.text ?? ''}
          maxLength={WITHDRAWAL_FIELD_LIMITS.postalCode}
          required
        />
        <div data-testid="withdrawal-country-field" className="relative z-10">
          <p className="mb-[0.5rem] block text-[1rem] font-bold text-gray-900">
            {tile['country-label']?.text} <span className="text-primary-100">*</span>
          </p>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <ListSelect
                onChange={field.onChange}
                currentValue={field.value}
                list={countries ?? []}
                disabled={countries === null}
                defaultSelectLabel={tile['country-placeholder']?.text ?? ' '}
              />
            )}
          />
          {errors.country && <p className="text-sm text-red">{errors.country.message}</p>}
          {/* Without this hint an empty list would only ever surface as "Country is required". */}
          {countries?.length === 0 && (
            <RichTextRenderer blocks={tile['error-generic']} className="text-sm text-red" />
          )}
        </div>
      </div>
      <div className="space-y-1">
        <label
          htmlFor="consent"
          data-testid="withdrawal-consent-label"
          className="flex cursor-pointer items-center gap-2"
        >
          <input
            id="consent"
            type="checkbox"
            {...register('consent')}
            className="h-4 w-4 shrink-0 cursor-pointer"
            aria-describedby={errors.consent ? 'consent-error' : undefined}
          />
          <span className="text-sm text-neutral-60">
            <RichTextRenderer blocks={tile['consent-text']} />
          </span>
        </label>
        {errors.consent && (
          <p id="consent-error" className="text-sm text-red">
            {errors.consent.message}
          </p>
        )}
      </div>
      <div data-testid="withdrawal-captcha" className="relative z-0 max-w-sm">
        {captchaHook.CaptchaWidget(
          { className: 'relative pb-4' },
          { button: { border: '1px solid var(--neutral-50)' } },
        )}
      </div>
      <Button
        type="submit"
        disabled={!captchaSolution || captchaHook.captchaStatus.error !== null || isPending}
        loading={isPending}
        data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
      >
        {tile['button-text']?.text}
      </Button>
      {errorText && (
        <div role="alert" className="text-sm text-red">
          <RichTextRenderer blocks={errorText} />
        </div>
      )}
    </form>
  );
}

export function WithdrawalRequestFormTile({ tile, trackingPrefix }: TileProps) {
  const { stage } = useWithdrawalStage();

  if (stage !== 'request') {
    return null;
  }

  return (
    <Container className="overflow-visible">
      <div className="mx-auto max-w-5xl space-y-8">
        <div className="space-y-3 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            {formatPunctuation(tile.headline?.text)}
          </h1>
          <RichTextRenderer blocks={tile.subline} className="text-lg sm:text-xl" />
        </div>
        <WithdrawalRequestForm tile={tile} trackingPrefix={trackingPrefix} />
        {(tile['description-headline']?.text || tile['description-text']) && (
          <div className="space-y-2 border-t border-neutral-10 pt-6">
            {tile['description-headline']?.text && (
              <h2 className="text-lg font-bold">{tile['description-headline'].text}</h2>
            )}
            <RichTextRenderer blocks={tile['description-text']} />
          </div>
        )}
      </div>
    </Container>
  );
}
